<template>
  <router-link :to="`/workflow/${workflow.projectId}/${workflow.id}`">
    <div
      class="d-flex flex-column position-relative hover-container dashboard-card"
      :class="{
        'workflow-card' : hasThumbnailChild,
      }"
    >
      <v-menu v-if="!disabled && !hideActions">
        <template #activator="{ on, props }">
          <v-btn
            v-bind="props"
            variant="text"
            size="small"
            class="workflow-card-more-btn pa-0 show-on-hover"
            icon="mds:more_vert"
            v-on="{ ...on }"
            @click.prevent
          />
        </template>

        <v-list density="compact" data-cy="workflow-card-actions-list">
          <v-list-item
            @click="$emit('edit', workflow)"
            prepend-icon="mdi-pencil"
            title="Edit"
          />
          <v-list-item
            v-if="!workflowStatusCompleted"
            @click="showDeleteDialog = true"
            prepend-icon="mdi-delete-outline"
            title="Delete"
          />
          <v-tooltip location="bottom" v-if="workflowStatusCompleted">
            <template v-slot:activator="{ props }">
              <div v-bind="props">
                <v-list-item
                  prepend-icon="mdi-delete-outline"
                  title="Delete"
                  disabled
                />
              </div>
            </template>
            <span>Unlock the workflow under Workflow Settings to be able to delete it.</span>
          </v-tooltip>

          <!-- only show starred option on project dashboard -->
          <v-list-item
            v-if="$route.params.projectId"
            @click="toggleStarred()"
            prepend-icon="mdi-star"
            :title="workflow.starred ? 'Remove from starred' : 'Add to starred'"
          />

          <v-list-group
            @click.stop
          >
            <template #activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-content-copy"
                title="Create a copy in project:"
              />
            </template>

            <v-list-item
              v-for="(projectMembership, index) in projectMemberships"
              :key="index"
              :disabled="processingRequest === workflow.id"
              @click="callCloneWorkflow(projectMembership.project.id)"
              prepend-icon="mdi-folder-outline"
            >
              <v-list-item-title>
                {{ projectMembership.project?.name }}
                {{ projectMembership.project?.id == $route.params.projectId ? '(this project)' : '' }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>
        </v-list>
      </v-menu>

      <template v-if="hasThumbnailChild">
        <div v-html="sanitizedThumbnail"></div>
        <v-spacer />
        <v-divider class="my-0" />
        <div class="project-name-thumbnail text-truncate pa-2" style="line-height: 11px;">
          {{ $route.params.projectId ? '' : `${workflow.projectName} - ` }}{{ workflow.name }}
        </div>
        <div class="d-flex flex-nowrap" style="background-color: var(--light-1);">
          <div class="project-name-thumbnail text-truncate mb-2 ml-2" style="color: var(--dark-1); font-size: 11px;">
            {{ $route.params.projectId ? workflow.projectName : workflow.organisationName }}
          </div>
          <v-spacer class="mx-2" />
          <div class="v-spacer mx-2" style="background-color: var(--light-1);"></div>
          <div class="project-name text-truncate" v-if="workflow.updatedAt"
               style="margin: 0 8px 8px 0; color: var(--dark-1);  font-size: 11px;">
            Last modified: {{ formatDistanceToNowStrict(new Date(workflow.updatedAt)) }} ago
          </div>
        </div>
      </template>

      <template v-else>
        <img
          width="307"
          height="40"
          src="@/assets/workflow-placeholder.svg"
          alt="workflow placeholder"
        />

        <v-spacer />

        <div class="project-name text-truncate">
          {{ workflow.projectName }}
        </div>
        <div class="workflow-name">
          {{ workflow.name }}
        </div>

        <v-divider class="my-4" />

        <div class="d-flex flex-nowrap">
          <div class="project-name text-truncate">
            {{ workflow.organisationName }}
          </div>
          <v-spacer class="mx-2" />
          <div class="project-name text-truncate" v-if="workflow.updatedAt">
            Last modified: {{ formatDistanceToNowStrict(new Date(workflow.updatedAt)) }} ago
          </div>
        </div>
      </template>
      <delete-confirmation-dialog
        v-model="showDeleteDialog"
        title="Confirm workflow deletion"
        text="Are you sure you want to delete this workflow including all of its content such as events, cards, comments, votes, etc? This operation cannot be undone."
        :onDelete="deleteWorkflowAction"
      />
    </div>
  </router-link>
</template>

<script>
import workflowApi, { deleteWorkflow, cloneWorkflow } from '@/api/workflowApi'
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict'
import { mapState } from 'vuex'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog'
import { workflowHelpers } from '@/mixins/workflowHelpers.js'
import { sanitizeSvg } from '@/utils/sanitize'

export default {
  mixins: [workflowHelpers],
  components: {
    DeleteConfirmationDialog
  },
  props: {
    workflow: Object,
    disabled: Boolean,
    hideActions: Boolean
  },

  data () {
    return {
      processingRequest: null,
      showDeleteDialog: false
    }
  },

  methods: {
    toggleStarred () {
      const starred = !this.workflow.starred
      workflowApi.updateWorkflowNoHistoryMutation(
        this.workflow.projectId,
        {
          id: this.workflow.id,
          expectedVersion: this.workflow.version,
          starred
        }
      )
    },
    async callCloneWorkflow (targetProjectId) {
      this.processingRequest = this.workflow.id
      await this.cloneWorkflow(targetProjectId, this.workflow.id)
      this.processingRequest = null
    },
    async deleteWorkflowAction () {
      await deleteWorkflow(this.workflow.id, this.workflow.projectId)
    },
    cloneWorkflow,
    formatDistanceToNowStrict,
    hasChildElement (svgString) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(svgString, 'image/svg+xml')
      const svgElement = doc.documentElement
      return svgElement.children.length > 0
    }
  },
  computed: {
    ...mapState({
      projectMemberships: state => state.projectMemberships.projectMemberships
    }),
    hasThumbnailChild () {
      return this.workflow.thumbnail && this.hasChildElement(this.workflow.thumbnail)
    },
    sanitizedThumbnail () {
      return sanitizeSvg(this.workflow.thumbnail)
    }
  }
}
</script>

<style scoped>
.project-name {
  color: var(--text-light);
  font-size: 12px;
  line-height: 15px;
  z-index: 1;
}

.workflow-name {
  color: var(--text-dark);
  font-size: 20px;
  line-height: 30px;
  letter-spacing: -0.6px;
  z-index: 1;
  max-height: 50px;
  overflow: hidden;
}

.workflow-card-more-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}

.star-icon {
  position: absolute;
  left: 6px;
  top: 6px;
}

a {
  text-decoration: none;
  color: currentColor;
}

.dashboard-card {
  padding: 12px;
  cursor: pointer;

  width: 100%;
  overflow: hidden;
  height: 206px;

  border-radius: 6px;
  border: 1px solid var(--light-1);
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);

  &.workflow-card {
    padding: 0;
  }

  &:hover {
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
  }
}
</style>
